/**
 * HistoryItemReplay - Component for displaying history item with replay parity
 * 
 * V2.4: When caseId is available, uses ExplainabilityTreeWrapper to fetch backend
 * audit-grade data instead of just showing stored history fields.
 * 
 * This ensures replay path uses the same data as the live UI path.
 */
import React, { useState } from 'react';
import { ExplainabilityTreeWrapper } from './ExplainabilityTreeWrapper';
import { Collapse, Badge, Stack, Group, Text, Button, Paper } from '@mantine/core';
import { confidencePercent as toConfidencePercent } from '../utils/confidence';

interface HistoryItemReplayProps {
  item: {
    _id: string;
    hs_code: string;
    description: string;
    confidence: string;
    confidence_score: number;
    gir_path?: string[];
    legal_basis?: string;
    reasoning?: string;
    status?: string;
    created_at: number;
    caseId?: string;
  };
  locale: string;
  onExportPdf: () => void;
  exportLabel: string;
}

/**
 * Displays a history item with optional expanded detail view.
 * If caseId exists, the expanded view uses ExplainabilityTreeWrapper for backend parity.
 */
export function HistoryItemReplay({ item, locale, onExportPdf, exportLabel }: HistoryItemReplayProps) {
  const [expanded, setExpanded] = useState(false);

  const confidencePercent = Math.round(toConfidencePercent(item.confidence_score));
  
  const hasBackendParity = Boolean(item.caseId);

  return (
    <Paper shadow="xs" p="xs" withBorder className="history-item-replay" data-testid="history-item">
      <Group justify="space-between" mb="xs">
        <Stack gap={2}>
          <Group gap="xs">
            <Text fw={600} size="lg" data-testid="history-item-hs-code">{item.hs_code}</Text>
            {hasBackendParity && (
              <Badge size="xs" color="green" variant="light">
                Audit-grade
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed" lineClamp={1} data-testid="history-item-description">
            {item.description}
          </Text>
        </Stack>
        <Stack gap={4} align="flex-end">
          <Text size="sm" c="dimmed">
            {new Date(item.created_at).toLocaleDateString(locale)}
          </Text>
          <Badge color={confidencePercent >= 85 ? 'green' : confidencePercent >= 70 ? 'yellow' : 'red'}>
            {confidencePercent}%
          </Badge>
        </Stack>
      </Group>

      <Group gap="xs">
        {hasBackendParity && (
          <Button 
            size="xs" 
            variant="light"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? '▲ Sakrij detalje' : '▼ Prikaži detalje'}
          </Button>
        )}
        <Button
          size="xs"
          variant="outline"
          onClick={onExportPdf}
          disabled
          title="Official verified report is not available in this release."
        >
          {exportLabel}
        </Button>
      </Group>

      {hasBackendParity && (
        <Collapse in={expanded}>
          <Paper p="sm" mt="sm" bg="gray.0" radius="sm">
            <Text size="xs" c="dimmed" mb="xs">
              ℹ️ Prikazani podaci učitani iz audit log-a (backend istina)
            </Text>
            <ExplainabilityTreeWrapper 
              data={{ 
                caseId: item.caseId,
                hs_code: item.hs_code,
                description: item.description,
              }} 
              description={item.description} 
            />
          </Paper>
        </Collapse>
      )}

      {!hasBackendParity && expanded && (
        <Paper p="sm" mt="sm" bg="yellow.0" radius="sm">
          <Text size="xs" c="orange.8">
            ⚠️ Ova klasifikacija nema povezani audit case. Prikazani su samo spremljeni podaci.
          </Text>
          {item.gir_path && item.gir_path.length > 0 && (
            <Text size="sm" mt="xs">
              <strong>GIR putanja:</strong> {item.gir_path.join(' → ')}
            </Text>
          )}
          {item.legal_basis && (
            <Text size="sm" mt="xs">
              <strong>Pravna osnova:</strong> {item.legal_basis}
            </Text>
          )}
        </Paper>
      )}
    </Paper>
  );
}

export default HistoryItemReplay;
